import { TYPE_COLORS } from 'utils/constants';

export interface ChangelogItem {
  type: keyof typeof TYPE_COLORS;
  priority: number;
  enShort: string;
  esShort: string;
  en: string;
  es: string;
}

export interface ChangelogEntry {
  version: string;
  date: string;
  items: ChangelogItem[];
}

export const CHANGELOG: ChangelogEntry[] = [
  {
    version: "3.0.4",
    date: "2026-08-10",
    items: [
      {
        type: "fix", priority: 5,
        enShort: "Fixed broken links to profiles that don't exist.",
        esShort: "Corregidos los links a perfiles que no existen.",
        en: "Visiting a profile that doesn't exist (a broken or mistyped link) now correctly shows the not found page instead of a blank page.",
        es: "Visitar un perfil que no existe (un link roto o mal escrito) ahora muestra correctamente la página de no encontrado en vez de una página en blanco.",
      },
      {
        type: "improvement", priority: 3,
        enShort: "Added a retry option if a profile fails to load.",
        esShort: "Se agregó una opción de reintentar si un perfil falla al cargar.",
        en: "If a profile page fails to load due to a connection issue, you'll now see a clear message with a retry button instead of a blank page.",
        es: "Si una página de perfil falla al cargar por un problema de conexión, ahora vas a ver un mensaje claro con un botón para reintentar en vez de una página en blanco.",
      },
    ],
  },
  {
    version: "3.0.3",
    date: "2026-08-02",
    items: [
      {
        type: "fix", priority: 6,
        enShort: "Fixed public profile pages showing not found.",
        esShort: "Corregido que los perfiles públicos mostraran no encontrado.",
        en: "Fixed public profile pages (linkerto.com/yourname) sometimes showing a page not found error instead of loading.",
        es: "Se corrigió que los perfiles públicos (linkerto.com/tunombre) a veces mostraran un error de página no encontrada en vez de cargar.",
      },
    ],
  },
  {
    version: "3.0.2",
    date: "2026-08-02",
    items: [
      {
        type: "improvement", priority: 2,
        enShort: "Better search engine visibility.",
        esShort: "Mejor visibilidad en buscadores.",
        en: "Improved how Linkerto shows up on Google and other search engines: cleaner metadata, a sitemap, and structured data.",
        es: "Mejoramos cómo aparece Linkerto en Google y otros buscadores: metadata más clara, un sitemap y datos estructurados.",
      },
    ],
  },
  {
    version: "3.0.1",
    date: "2026-07-26",
    items: [
      {
        type: "fix", priority: 3,
        enShort: "Fixed the profile health card action.",
        esShort: "Corregida la acción de la tarjeta de salud del perfil.",
        en: "Fixed the profile completion card sometimes sending you to the wrong section instead of what's actually missing.",
        es: "Se corrigió que la tarjeta de completitud del perfil a veces te mandara a la sección equivocada en vez de a lo que realmente falta.",
      },
      {
        type: "improvement", priority: 2,
        enShort: "WhatsApp added as a social option.",
        esShort: "WhatsApp agregado como opción social.",
        en: "WhatsApp is now available as a social link option.",
        es: "WhatsApp ya está disponible como opción de red social.",
      },
    ],
  },
  {
    version: "3.0.0",
    date: "2026-07-25",
    items: [
      {
        type: "feature", priority: 10,
        enShort: "Linkerto is reborn.",
        esShort: "Linkerto renace.",
        en: "Linkerto is reborn. Completely redesigned from the ground up.",
        es: "Linkerto renace. Rediseñado por completo desde cero.",
      },
      {
        type: "feature", priority: 9,
        enShort: "New customization engine.",
        esShort: "Nuevo motor de personalización.",
        en: "New customization engine — your profile, your rules.",
        es: "Nuevo motor de personalización — tu perfil, tus reglas.",
      },
      {
        type: "feature", priority: 8,
        enShort: "Live analytics for your profile.",
        esShort: "Analíticas en tiempo real.",
        en: "Live analytics: see who visits you, when, and from where.",
        es: "Analíticas en tiempo real: ve quién te visita, cuándo y desde dónde.",
      },
      {
        type: "feature", priority: 7,
        enShort: "Rich preview when you share your link.",
        esShort: "Vista previa al compartir tu link.",
        en: "Your Linkerto link now shows a rich preview (title, description and image) when shared on WhatsApp, X, Slack and more.",
        es: "Tu link de Linkerto ahora muestra una vista previa (título, descripción e imagen) al compartirlo en WhatsApp, X, Slack y más.",
      },
      {
        type: "improvement", priority: 6,
        enShort: "Consistent profile everywhere you preview it.",
        esShort: "Perfil consistente en cada vista previa.",
        en: "Your public profile now looks and scales the same across the live preview, the preview tab and your published page.",
        es: "Tu perfil público ahora se ve y escala igual en la vista previa en vivo, la pestaña de preview y tu página publicada.",
      },
      {
        type: "improvement", priority: 5,
        enShort: "Blazing-fast load times.",
        esShort: "Carga ultrarrápida.",
        en: "Blazing-fast load times across all devices.",
        es: "Tiempos de carga ultrarrápidos en todos los dispositivos.",
      },
      {
        type: "improvement", priority: 5,
        enShort: "Smoother public profile load.",
        esShort: "Carga más fluida del perfil público.",
        en: "Fixed background flicker and improved the loading state on your public profile.",
        es: "Se corrigió el parpadeo del fondo y se mejoró el estado de carga en tu perfil público.",
      },
      {
        type: "improvement", priority: 4,
        enShort: "Refined dark theme and new accents.",
        esShort: "Tema oscuro refinado y nuevos acentos.",
        en: "Refined dark theme and new accent system.",
        es: "Tema oscuro refinado y nuevo sistema de acentos.",
      },
      {
        type: "fix", priority: 3,
        enShort: "Fixed avatar initials in the dashboard.",
        esShort: "Corregidas las iniciales del avatar en el panel.",
        en: "Fixed your avatar showing blank in the dashboard sidebar when you have no profile photo — it now shows your initials.",
        es: "Se corrigió que el avatar apareciera en blanco en el panel cuando no tienes foto de perfil; ahora muestra tus iniciales.",
      },
      {
        type: "fix", priority: 3,
        enShort: "Fixed stuck hover on mobile.",
        esShort: "Corregido el hover atorado en móvil.",
        en: "Fixed link cards staying highlighted after tapping them on mobile.",
        es: "Se corrigió que las tarjetas de links se quedaran resaltadas al tocarlas en móvil.",
      },
      {
        type: "fix", priority: 2,
        enShort: "Fixed homepage intro animation.",
        esShort: "Corregida la animación de inicio.",
        en: "Fixed the homepage intro animation sometimes starting from the wrong position.",
        es: "Se corrigió que la animación de inicio del home a veces empezara desde una posición incorrecta.",
      },
      {
        type: "improvement", priority: 2,
        enShort: "Better carousel on large screens.",
        esShort: "Mejor carrusel en pantallas grandes.",
        en: "The homepage carousel now shows multiple profiles at once on large monitors instead of one giant image.",
        es: "El carrusel del home ahora muestra varios perfiles a la vez en monitores grandes en lugar de una sola imagen gigante.",
      },
      {
        type: "improvement", priority: 2,
        enShort: "Rebuilt core architecture.",
        esShort: "Arquitectura central reconstruida.",
        en: "Rebuilt core architecture for a smoother, more stable experience.",
        es: "Arquitectura central reconstruida para una experiencia más fluida y estable.",
      },
    ],
  },
  {
    version: "2.9.1",
    date: "2026-04-26",
    items: [
      {
        type: "feature", priority: 10,
        enShort: "New analytics dashboard.",
        esShort: "Nuevo panel de analíticas.",
        en: "New analytics dashboard released.",
        es: "Nuevo panel de analíticas disponible.",
      },
      {
        type: "improvement", priority: 6,
        enShort: "New themes available.",
        esShort: "Nuevos temas disponibles.",
        en: "New themes available.",
        es: "Nuevos temas disponibles.",
      },
      {
        type: "improvement", priority: 2,
        enShort: "Performance improvements.",
        esShort: "Mejoras de rendimiento.",
        en: "Performance improvements.",
        es: "Mejoras de rendimiento.",
      },
    ],
  },
];

export const getLatestEntry = (): ChangelogEntry => CHANGELOG[0];

export const getLatestCardItems = (): ChangelogItem[] =>
  [...CHANGELOG[0].items]
    .sort((a, b) => b.priority - a.priority)
    .slice(0, 3);
